import { useMutation, useQueryClient } from '@tanstack/react-query'
import { authApi } from '../api/auth.api'
import { useAuthStore } from '../stores/auth.store'

/**
 * Logs out: invalidates the server session, aborts every in-flight query so
 * the app stops fetching immediately, then clears the local session.
 *
 * The query cache is intentionally NOT cleared in place. Every logout path in
 * the app finishes with a hard redirect (`window.location.replace` — here in
 * the Sidebar and in the axios `forceLogout`), which tears down the whole SPA,
 * cache included. Calling `queryClient.clear()` while the authenticated pages
 * are still mounted does the opposite of "stop loading": wiping the cache makes
 * those mounted observers immediately *refetch*, now token-less, producing a
 * burst of doomed 401s — exactly the "still loading on logout" symptom.
 * Cancelling in-flight requests and letting the redirect discard the cache
 * avoids it.
 */
export function useLogout() {
  const clearSession = useAuthStore((s) => s.clearSession)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: authApi.logout,
    // The instant the user commits to leaving, abort every in-flight request so
    // nothing keeps loading. The logout POST itself still carries the token —
    // the session is cleared only once it settles.
    onMutate: () => {
      void queryClient.cancelQueries()
    },
    // Clear locally even if the server call fails, and cancel once more to kill
    // anything that started during the POST.
    onSettled: () => {
      clearSession()
      void queryClient.cancelQueries()
    },
  })
}
